import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "components/Layout";
import api from "utils/api";
import { setLogin } from "state";
import "styles/auth.css";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  acceptedTerms: false,
};

const RegisterPage = ({ source = "website", title, lead, kicker = "Konto erstellen" }) => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/buchen";

  const handleChange = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setInfo(null);
    if (form.password !== form.passwordConfirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (!form.acceptedTerms) {
      setError("Bitte akzeptiere die Datenschutzhinweise.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        source,
        brand: "dr-hoffmann",
      };
      const { data } = await api.post("/auth/register", payload);
      if (data.emailSent === false || (data.emailSent === undefined && data.user?.emailVerified === false)) {
        setInfo(data.message || "Bitte bestätige deine E-Mail-Adresse.");
      }
      // If email verification is disabled the server still returns the user.
      // We need to do a follow-up login to get a token.
      if (data.user?.emailVerified) {
        try {
          const loginRes = await api.post("/auth/login", {
            email: form.email,
            password: form.password,
          });
          dispatch(setLogin({ token: loginRes.data.token, user: loginRes.data.user }));
          navigate(next, { replace: true });
          return;
        } catch {
          /* fall through to info path */
        }
      }
      setInfo((current) => current || data.message);
      setForm(initialForm);
    } catch (err) {
      setError(err?.response?.data?.message || "Registrierung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="auth-page">
        <div className="auth-card">
          <div className="auth-kicker">{kicker}</div>
          <h1>{title || (<>Neues <em>Konto</em></>)}</h1>
          <p className="auth-lead">
            {lead ||
              "Registriere dich, um Studio- und Vor-Ort-Termine zu buchen, Buchungen zu ändern und Erinnerungen per E-Mail zu erhalten."}
          </p>

          {error && <div className="auth-alert is-error">{error}</div>}
          {info && <div className="auth-alert is-success">{info}</div>}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div className="row">
              <div>
                <label htmlFor="reg-first">Vorname</label>
                <input
                  id="reg-first"
                  required
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={handleChange("firstName")}
                />
              </div>
              <div>
                <label htmlFor="reg-last">Nachname</label>
                <input
                  id="reg-last"
                  required
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={handleChange("lastName")}
                />
              </div>
            </div>
            <div>
              <label htmlFor="reg-email">E-Mail</label>
              <input
                id="reg-email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={handleChange("email")}
              />
            </div>
            <div>
              <label htmlFor="reg-phone">Telefonnummer</label>
              <input
                id="reg-phone"
                type="tel"
                required
                autoComplete="tel"
                placeholder="+49 ..."
                value={form.phone}
                onChange={handleChange("phone")}
              />
            </div>
            <div className="row">
              <div>
                <label htmlFor="reg-pw">Passwort</label>
                <input
                  id="reg-pw"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={form.password}
                  onChange={handleChange("password")}
                />
              </div>
              <div>
                <label htmlFor="reg-pw2">Passwort wiederholen</label>
                <input
                  id="reg-pw2"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={form.passwordConfirm}
                  onChange={handleChange("passwordConfirm")}
                />
              </div>
            </div>

            <label style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginTop: "0.25rem", letterSpacing: 0, fontSize: "0.85rem", color: "var(--grey-mid)" }}>
              <input
                type="checkbox"
                checked={form.acceptedTerms}
                onChange={handleChange("acceptedTerms")}
                style={{ width: "auto", marginTop: "0.2rem" }}
              />
              <span>
                Ich habe die <Link to="/datenschutz">Datenschutzhinweise</Link>{" "}
                gelesen und stimme der Verarbeitung meiner Daten zur Terminbuchung zu.
              </span>
            </label>

            <div className="auth-actions">
              <button type="submit" className="auth-submit" disabled={submitting}>
                {submitting ? "Registrieren..." : "Konto anlegen"}
              </button>
            </div>
          </form>

          <div className="auth-links">
            <span>
              Bereits registriert?{" "}
              <Link to={`/einloggen${next !== "/konto" ? `?next=${encodeURIComponent(next)}` : ""}`}>
                Jetzt anmelden →
              </Link>
            </span>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RegisterPage;
